import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WrenchScrewdriverIcon, CalendarDaysIcon, UserIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import deviceMaintenanceService, { DeviceMaintenance } from '../../services/deviceMaintenanceService';
import { formatDateTime } from '../../utils/dateUtils';
import { usePermission } from '../../hooks/usePermission';

interface MaintenanceHistorySectionProps {
  rentalId: number;
}

const MaintenanceHistorySection: React.FC<MaintenanceHistorySectionProps> = ({ rentalId }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  /**
   * การ์ดนี้ยิง `GET /device-maintenance/search/` ซึ่ง backend gate ด้วย `maintenance.view`
   * — **ต้องเช็คก่อนยิง ไม่ใช่เช็คแค่ตอนเรนเดอร์ลิงก์** (BUG-UI-09): เดิม `useEffect` มีเงื่อนไข
   * แค่ `if (rentalId)` → ทุก role ที่มี `rentals.view` แต่ไม่มี `maintenance.view`
   * (dev = Accounting · **prod = กลุ่มบัญชี/amintra ซึ่งใช้ระบบมากที่สุด**) เปิดหน้ารายละเอียด
   * การเช่าแล้วได้ 403 สองครั้งทุกครั้ง แถมการ์ดขึ้นข้อความ "ไม่สามารถโหลดประวัติซ่อมบำรุงได้"
   * ซึ่งอ่านเหมือนระบบพัง ทั้งที่เป็นเรื่องสิทธิ์ปกติ
   */
  const canViewMaintenance = hasPermission('maintenance.view');
  const [records, setRecords] = useState<DeviceMaintenance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const fetchMaintenanceHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await deviceMaintenanceService.getMaintenanceRecords({
          rental_id: rentalId,
          limit: 5 // แสดงแค่ 5 รายการล่าสุด
        });
        setRecords(response.items);
        setTotal(response.total);
      } catch (err) {
        console.error('Failed to fetch maintenance history:', err);
        setError('ไม่สามารถโหลดประวัติซ่อมบำรุงได้');
      } finally {
        setLoading(false);
      }
    };

    if (!canViewMaintenance) {
      // ไม่มีสิทธิ์ = ไม่ยิง และไม่ค้างอยู่ที่สถานะ loading (การ์ดถูกซ่อนทั้งใบอยู่แล้ว)
      setLoading(false);
      return;
    }
    if (rentalId) {
      fetchMaintenanceHistory();
    }
  }, [rentalId, canViewMaintenance]);

  const handleViewAll = () => {
    // Navigate to maintenance page with rental_id filter
    // ประวัติทั้งสัญญา = รวมเคสที่ปิดแล้ว (หน้ารายการเริ่มที่เฉพาะเคสที่ยังไม่ปิด)
    navigate(`/maintenance/list?rental_id=${rentalId}&closed=1`);
  };

  // ไม่มีสิทธิ์ดูข้อมูลซ่อมบำรุง → ซ่อนการ์ดทั้งใบ (ไม่ใช่โชว์การ์ดว่าง/ข้อความ error)
  // pattern เดียวกับการ์ด "ใบงานเตรียมเครื่อง" ในหน้าเดียวกัน
  if (!canViewMaintenance) return null;

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
        <div className="px-6 py-4 border-b border-secondary-200">
          <h3 className="text-lg font-medium text-secondary-900 flex items-center">
            <WrenchScrewdriverIcon className="w-5 h-5 mr-2 text-info-500" />
            ประวัติการซ่อมบำรุง
          </h3>
        </div>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-info-500"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
        <div className="px-6 py-4 border-b border-secondary-200">
          <h3 className="text-lg font-medium text-secondary-900 flex items-center">
            <WrenchScrewdriverIcon className="w-5 h-5 mr-2 text-info-500" />
            ประวัติการซ่อมบำรุง
          </h3>
        </div>
        <div className="p-6">
          <div className="text-center py-8 text-danger-500">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (records.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
        <div className="px-6 py-4 border-b border-secondary-200">
          <h3 className="text-lg font-medium text-secondary-900 flex items-center">
            <WrenchScrewdriverIcon className="w-5 h-5 mr-2 text-info-500" />
            ประวัติการซ่อมบำรุง
          </h3>
        </div>
        <div className="p-6">
          <div className="text-center py-8 text-secondary-500">
            <p>ยังไม่มีประวัติการซ่อมบำรุง</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-secondary-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-secondary-900 flex items-center">
            <WrenchScrewdriverIcon className="w-5 h-5 mr-2 text-info-500" />
            ประวัติการซ่อมบำรุง
          </h3>
          {/* G-12: ปุ่มพาไป /maintenance/list — แสดงเมื่อมีสิทธิ์ maintenance.view เท่านั้น */}
          {total > 5 && hasPermission('maintenance.view') && (
            <button
              onClick={handleViewAll}
              className="text-info-600 hover:text-info-800 text-sm font-medium"
            >
              ดูทั้งหมด ({total}) →
            </button>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="p-6">
        <div className="space-y-4">
          {records.map((record, index) => (
            <div key={record.id} className="relative pl-6 border-l-2 border-secondary-200">
              {/* Timeline dot */}
              <div className="absolute -left-2 top-0 w-4 h-4 bg-info-500 rounded-full border-2 border-white"></div>

              {/* Content */}
              <div className="pb-4">
                {/* Date */}
                <p className="text-sm text-secondary-500 mb-1 flex items-center">
                  <CalendarDaysIcon className="w-4 h-4 mr-1" />
                  {formatDateTime(record.maintenance_date)}
                </p>

                {/* Main info */}
                <div className="bg-secondary-50 rounded-lg p-3">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {/* Maintenance Type */}
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-info-100 text-info-800">
                      {record.maintenance_type?.name || 'ไม่ระบุประเภท'}
                    </span>

                    {/* Repair Method */}
                    {record.repair_method && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-800">
                        {record.repair_method.name}
                      </span>
                    )}

                    {/* Result */}
                    {record.result && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800">
                        {record.result}
                      </span>
                    )}
                  </div>

                  {/* Performer */}
                  <p className="text-sm text-secondary-600 flex items-center">
                    <UserIcon className="w-4 h-4 mr-1" />
                    ผู้ดำเนินการ: {record.performed_by || 'ยังไม่มีผู้รับเรื่อง'}
                  </p>

                  {/* Details */}
                  {record.details && (
                    <p className="text-sm text-secondary-500 mt-1 flex items-start">
                      <DocumentTextIcon className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                      <span>{record.details}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-4 border-t border-secondary-200 text-center text-sm text-secondary-500">
          แสดง {records.length} รายการล่าสุด {total > records.length && `จากทั้งหมด ${total} รายการ`}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceHistorySection;
